import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, Star, Truck, RotateCcw, CreditCard, Headphones } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 8: MEHAK - Garden Bloom
 * Style: Feminine, floral, romantic Indian fashion with rose/pink accents
 * Best for: Designer Sarees, Anarkalis, Bridal Lehengas, Festive ethnic wear
 * Deep rose (#BE185D) accents on white with light pink surfaces
 */
const Template8 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const [email, setEmail] = useState('');
    const touchStartX = useRef(0);

    // Collections
    const collections = [
        {
            name: 'Wedding Season',
            description: 'Bridal lehengas and wedding ensembles adorned with intricate embroidery and timeless grace.',
            image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
        {
            name: 'Festive Wear',
            description: 'Celebrate every occasion in stunning Anarkalis, sarees, and kurtas that bloom with festive spirit.',
            image: 'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
        {
            name: 'Everyday Grace',
            description: 'Effortless elegance for the modern woman. Light fabrics, soft hues, and contemporary silhouettes.',
            image: 'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
    ];

    // Products
    const products = [
        { id: 1, name: 'Rose Silk Anarkali', price: '\u20B97,499', badge: 'NEW', image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 2, name: 'Designer Banarasi Saree', price: '\u20B912,999', image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Bridal Lehenga Set', price: '\u20B924,999', badge: 'BESTSELLER', image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 4, name: 'Festive Palazzo Suit', price: '\u20B94,999', image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 5, name: 'Chanderi Silk Dupatta Set', price: '\u20B96,499', badge: 'NEW', image: 'https://images.pexels.com/photos/6712360/pexels-photo-6712360.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 6, name: 'Embroidered Anarkali Gown', price: '\u20B98,999', image: 'https://images.pexels.com/photos/7275385/pexels-photo-7275385.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 7, name: 'Zari Work Wedding Saree', price: '\u20B918,500', image: 'https://images.pexels.com/photos/8953474/pexels-photo-8953474.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 8, name: 'Floral Print Kurta Set', price: '\u20B92,499', image: 'https://images.pexels.com/photos/2955375/pexels-photo-2955375.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    // Features
    const features = [
        { icon: Truck, title: 'Free Shipping', desc: 'On orders above \u20B91,999' },
        { icon: RotateCcw, title: 'Easy Returns', desc: '7-day hassle-free returns' },
        { icon: CreditCard, title: 'COD Available', desc: 'Cash on delivery across India' },
        { icon: Headphones, title: '24/7 Support', desc: 'Dedicated customer care' },
    ];

    // Testimonials
    const testimonials = [
        {
            text: 'The most beautiful Anarkali I have ever owned. The rose embroidery is delicate yet stunning. MEHAK truly understands feminine elegance.',
            name: 'Priya Sharma',
            city: 'Jaipur',
            rating: 5,
        },
        {
            text: 'My bridal lehenga from MEHAK was a dream come true. Every detail was perfect, from the zari work to the soft silk fabric. I felt like a garden in bloom!',
            name: 'Sneha Patel',
            city: 'Mumbai',
            rating: 5,
        },
        {
            text: 'I keep coming back to MEHAK for all my festive wear. The quality is outstanding and the designs are both modern and rooted in tradition.',
            name: 'Kavitha Reddy',
            city: 'Hyderabad',
            rating: 5,
        },
    ];

    // Auto-rotate testimonials
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    // Touch swipe for testimonials
    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) => diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length);
        }
    };

    const toggleWishlist = (e, id) => { e.stopPropagation(); setWishlist((prev) => ({ ...prev, [id]: !prev[id] })); };

    const handleSubscribe = (e) => { e.preventDefault(); if (email.trim()) setEmail(''); };

    return (
        <div style={{ fontFamily: "'DM Sans', sans-serif" }} className="w-full min-h-screen bg-white">
            {/* Google Fonts */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&family=DM+Sans:wght@300;400;500;700&display=swap');
            `}</style>

            {/* ===== SECTION 1: ASYMMETRIC SPLIT HERO ===== */}
            {/* Mobile: stacked - image on top, text below */}
            <section className="lg:hidden" style={{ backgroundColor: '#FFFFFF' }}>
                <div className="relative w-full aspect-[4/5] overflow-hidden">
                    <img
                        src="https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=1200"
                        alt="Indian fashion model in elegant ethnic wear"
                        className="w-full h-full object-cover"
                    />
                    <div
                        className="absolute inset-0"
                        style={{ background: 'linear-gradient(to bottom, transparent 50%, rgba(253,242,248,0.9) 100%)' }}
                    />
                </div>
                <div className="px-5 py-8 text-center" style={{ backgroundColor: '#FDF2F8' }}>
                    <p
                        className="text-xs tracking-[0.3em] uppercase mb-3 font-medium"
                        style={{ color: '#BE185D', fontFamily: "'DM Sans', sans-serif" }}
                    >
                        New Collection 2026
                    </p>
                    <h1
                        className="text-3xl font-bold mb-4 leading-tight"
                        style={{ fontFamily: "'Libre Baskerville', serif", color: '#1A1A1A' }}
                    >
                        Bloom Into Elegance
                    </h1>
                    <p className="text-sm mb-6 leading-relaxed max-w-sm mx-auto" style={{ color: '#6B7280' }}>
                        Discover handcrafted Indian fashion that celebrates femininity, grace, and the timeless beauty of every bloom.
                    </p>
                    <button
                        onClick={() => navigate('/template8/shop')}
                        className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-semibold tracking-wider uppercase rounded-lg transition-all duration-300"
                        style={{ backgroundColor: '#BE185D', color: '#FFFFFF' }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#9D174D'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#BE185D'; }}
                    >
                        Shop Now <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </section>

            {/* Desktop: Asymmetric split - 60% image left, 40% rose panel right */}
            <section className="hidden lg:flex min-h-screen" style={{ backgroundColor: '#FFFFFF' }}>
                {/* Left: Large Image (60%) */}
                <div className="w-[60%] relative overflow-hidden">
                    <img
                        src="https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=1200"
                        alt="Indian fashion model in elegant ethnic wear"
                        className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-700"
                    />
                </div>

                {/* Right: Rose Panel with text + CTA (40%) */}
                <div
                    className="w-[40%] flex flex-col justify-center px-12 xl:px-16"
                    style={{ backgroundColor: '#FDF2F8' }}
                >
                    <div className="w-16 h-1 rounded-full mb-8" style={{ backgroundColor: '#BE185D' }} />
                    <p
                        className="text-sm tracking-[0.3em] uppercase mb-4 font-medium"
                        style={{ color: '#BE185D', fontFamily: "'DM Sans', sans-serif" }}
                    >
                        New Collection 2026
                    </p>
                    <h1
                        className="text-4xl xl:text-5xl font-bold mb-6 leading-tight"
                        style={{ fontFamily: "'Libre Baskerville', serif", color: '#1A1A1A' }}
                    >
                        Bloom Into Elegance
                    </h1>
                    <p className="text-base mb-8 leading-relaxed" style={{ color: '#6B7280' }}>
                        Discover handcrafted Indian fashion that celebrates femininity, grace, and the timeless beauty of every bloom. Each piece is woven with love, tradition, and a touch of rose-kissed magic.
                    </p>
                    <div className="flex gap-4">
                        <button
                            onClick={() => navigate('/template8/shop')}
                            className="inline-flex items-center gap-2 px-8 py-4 text-sm font-semibold tracking-wider uppercase rounded-lg transition-all duration-300"
                            style={{ backgroundColor: '#BE185D', color: '#FFFFFF' }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#9D174D'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#BE185D'; }}
                        >
                            Shop Now <ArrowRight className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => navigate('/template8/about')}
                            className="inline-flex items-center gap-2 px-8 py-4 text-sm font-semibold tracking-wider uppercase rounded-lg transition-all duration-300"
                            style={{ backgroundColor: 'transparent', color: '#BE185D', border: '2px solid #BE185D' }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#BE185D'; e.currentTarget.style.color = '#FFFFFF'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#BE185D'; }}
                        >
                            Our Story
                        </button>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 2: CURATED COLLECTIONS ===== */}
            {/* Mobile: stacked full-width */}
            <section className="lg:hidden py-10" style={{ backgroundColor: '#FFFFFF' }}>
                <div className="px-4">
                    <p className="text-xs tracking-[0.3em] uppercase mb-2 text-center font-medium" style={{ color: '#BE185D' }}>
                        Curated For You
                    </p>
                    <h2
                        className="text-2xl font-bold mb-8 text-center"
                        style={{ fontFamily: "'Libre Baskerville', serif", color: '#1A1A1A' }}
                    >
                        Our Collections
                    </h2>
                    <div className="flex flex-col gap-4">
                        {collections.map((collection, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template8/shop')}
                                className="relative aspect-[3/4] overflow-hidden rounded-xl cursor-pointer group"
                            >
                                <img
                                    src={collection.image}
                                    alt={collection.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div
                                    className="absolute inset-0"
                                    style={{ background: 'linear-gradient(to top, rgba(157,23,77,0.85) 0%, rgba(190,24,93,0.2) 40%, transparent 100%)' }}
                                />
                                <div className="absolute bottom-0 left-0 right-0 p-6">
                                    <h3
                                        className="text-2xl font-bold mb-2"
                                        style={{ fontFamily: "'Libre Baskerville', serif", color: '#FFFFFF' }}
                                    >
                                        {collection.name}
                                    </h3>
                                    <p className="text-sm mb-3 leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>
                                        {collection.description}
                                    </p>
                                    <span className="text-xs tracking-[0.15em] uppercase inline-flex items-center gap-2 font-semibold" style={{ color: '#F9A8D4' }}>
                                        Explore <ArrowRight className="w-3 h-3" />
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Desktop: 3-column grid */}
            <section className="hidden lg:block py-24" style={{ backgroundColor: '#FFFFFF' }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <p className="text-sm tracking-[0.3em] uppercase mb-3 text-center font-medium" style={{ color: '#BE185D' }}>
                        Curated For You
                    </p>
                    <h2
                        className="text-4xl font-bold mb-16 text-center"
                        style={{ fontFamily: "'Libre Baskerville', serif", color: '#1A1A1A' }}
                    >
                        Our Collections
                    </h2>
                    <div className="grid grid-cols-3 gap-6">
                        {collections.map((collection, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template8/shop')}
                                className="relative aspect-[3/4] overflow-hidden rounded-xl cursor-pointer group"
                            >
                                <img
                                    src={collection.image}
                                    alt={collection.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div
                                    className="absolute inset-0"
                                    style={{ background: 'linear-gradient(to top, rgba(157,23,77,0.85) 0%, rgba(190,24,93,0.2) 40%, transparent 100%)' }}
                                />
                                <div className="absolute bottom-0 left-0 right-0 p-8">
                                    <h3
                                        className="text-3xl font-bold mb-3"
                                        style={{ fontFamily: "'Libre Baskerville', serif", color: '#FFFFFF' }}
                                    >
                                        {collection.name}
                                    </h3>
                                    <p className="text-sm mb-4 leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>
                                        {collection.description}
                                    </p>
                                    <span className="text-sm tracking-[0.15em] uppercase inline-flex items-center gap-2 group-hover:gap-3 transition-all duration-300 font-semibold" style={{ color: '#F9A8D4' }}>
                                        Explore <ArrowRight className="w-4 h-4" />
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 3: PRODUCT GRID ===== */}
            {/* Mobile: 2-col grid */}
            <section className="lg:hidden py-10" style={{ backgroundColor: '#FDF2F8' }}>
                <div className="px-4">
                    <h2
                        className="text-2xl font-bold mb-8 text-center"
                        style={{ fontFamily: "'Libre Baskerville', serif", color: '#1A1A1A' }}
                    >
                        Fresh Picks
                    </h2>
                    <div className="grid grid-cols-2 gap-3">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate(`/template8/product/${product.id}`)}
                                className="cursor-pointer group rounded-xl overflow-hidden"
                                style={{ backgroundColor: '#FFFFFF', transition: 'box-shadow 0.3s ease' }}
                            >
                                <div className="relative aspect-[3/4] overflow-hidden">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    {product.badge && (
                                        <span
                                            className="absolute top-2 left-2 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
                                            style={{ backgroundColor: '#F9A8D4', color: '#9D174D' }}
                                        >
                                            {product.badge}
                                        </span>
                                    )}
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                                        style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
                                    >
                                        <Heart
                                            className="w-4 h-4"
                                            style={{
                                                color: wishlist[product.id] ? '#BE185D' : '#6B7280',
                                                fill: wishlist[product.id] ? '#BE185D' : 'none',
                                            }}
                                        />
                                    </button>
                                </div>
                                <div className="p-3">
                                    <h3 className="text-xs font-medium line-clamp-1 mb-1" style={{ color: '#1A1A1A' }}>
                                        {product.name}
                                    </h3>
                                    <p className="text-xs font-bold" style={{ color: '#BE185D' }}>{product.price}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <button
                        onClick={() => navigate('/template8/shop')}
                        className="w-full mt-6 py-3.5 text-sm tracking-wider uppercase font-semibold rounded-lg transition-all duration-300"
                        style={{ backgroundColor: '#BE185D', color: '#FFFFFF' }}
                    >
                        View All Products
                    </button>
                </div>
            </section>

            {/* Desktop: 4-col grid */}
            <section className="hidden lg:block py-24" style={{ backgroundColor: '#FDF2F8' }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2
                        className="text-4xl font-bold mb-16 text-center"
                        style={{ fontFamily: "'Libre Baskerville', serif", color: '#1A1A1A' }}
                    >
                        Fresh Picks
                    </h2>
                    <div className="grid grid-cols-4 gap-6">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate(`/template8/product/${product.id}`)}
                                className="cursor-pointer group rounded-xl overflow-hidden"
                                style={{
                                    backgroundColor: '#FFFFFF',
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 8px 30px rgba(190,24,93,0.12)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                            >
                                <div className="relative aspect-[3/4] overflow-hidden">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    {product.badge && (
                                        <span
                                            className="absolute top-3 left-3 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
                                            style={{ backgroundColor: '#F9A8D4', color: '#9D174D' }}
                                        >
                                            {product.badge}
                                        </span>
                                    )}
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                                        style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
                                    >
                                        <Heart
                                            className="w-4 h-4"
                                            style={{
                                                color: wishlist[product.id] ? '#BE185D' : '#6B7280',
                                                fill: wishlist[product.id] ? '#BE185D' : 'none',
                                            }}
                                        />
                                    </button>
                                </div>
                                <div className="p-4">
                                    <h3 className="text-sm font-medium line-clamp-1 mb-2" style={{ color: '#1A1A1A' }}>
                                        {product.name}
                                    </h3>
                                    <p className="text-base font-bold" style={{ color: '#BE185D' }}>{product.price}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="text-center mt-12">
                        <button
                            onClick={() => navigate('/template8/shop')}
                            className="inline-flex items-center gap-2 px-10 py-4 text-sm font-semibold tracking-wider uppercase rounded-lg transition-all duration-300"
                            style={{ backgroundColor: 'transparent', color: '#BE185D', border: '2px solid #BE185D' }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#BE185D'; e.currentTarget.style.color = '#FFFFFF'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#BE185D'; }}
                        >
                            View All Products <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 4: FEATURES ===== */}
            <section className="py-12 lg:py-20" style={{ backgroundColor: '#FFFFFF' }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                        {features.map((feature, index) => (
                            <div key={index} className="text-center p-6 rounded-xl" style={{ backgroundColor: '#FDF2F8' }}>
                                <div
                                    className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
                                    style={{ backgroundColor: '#F9A8D4' }}
                                >
                                    <feature.icon className="w-6 h-6" style={{ color: '#BE185D' }} />
                                </div>
                                <h3 className="text-sm lg:text-base font-semibold mb-1" style={{ color: '#1A1A1A', fontFamily: "'DM Sans', sans-serif" }}>
                                    {feature.title}
                                </h3>
                                <p className="text-xs lg:text-sm" style={{ color: '#6B7280' }}>{feature.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 5: TESTIMONIALS ===== */}
            {/* Mobile: swipeable cards */}
            <section className="lg:hidden py-10" style={{ backgroundColor: '#FDF2F8' }}>
                <div className="px-4">
                    <p className="text-xs tracking-[0.3em] uppercase mb-2 text-center font-medium" style={{ color: '#BE185D' }}>
                        Love Letters
                    </p>
                    <h2
                        className="text-2xl font-bold mb-8 text-center"
                        style={{ fontFamily: "'Libre Baskerville', serif", color: '#1A1A1A' }}
                    >
                        What Our Blooms Say
                    </h2>
                    <div
                        className="relative overflow-hidden"
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        <div className="bg-white rounded-2xl p-6 shadow-sm" style={{ border: '1px solid #F9A8D4' }}>
                            <div className="flex gap-1 mb-4">
                                {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                                    <Star key={i} className="w-4 h-4" style={{ color: '#BE185D', fill: '#BE185D' }} />
                                ))}
                            </div>
                            <p className="text-sm leading-relaxed mb-4" style={{ color: '#1A1A1A', fontStyle: 'italic' }}>
                                "{testimonials[activeTestimonial].text}"
                            </p>
                            <div>
                                <p className="text-sm font-semibold" style={{ color: '#BE185D' }}>
                                    {testimonials[activeTestimonial].name}
                                </p>
                                <p className="text-xs" style={{ color: '#6B7280' }}>
                                    {testimonials[activeTestimonial].city}
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-center gap-2 mt-4">
                            {testimonials.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveTestimonial(i)}
                                    className="w-2 h-2 rounded-full transition-all"
                                    style={{ backgroundColor: i === activeTestimonial ? '#BE185D' : '#F9A8D4' }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Desktop: 3-col grid testimonials */}
            <section className="hidden lg:block py-24" style={{ backgroundColor: '#FDF2F8' }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <p className="text-sm tracking-[0.3em] uppercase mb-3 text-center font-medium" style={{ color: '#BE185D' }}>
                        Love Letters
                    </p>
                    <h2
                        className="text-4xl font-bold mb-16 text-center"
                        style={{ fontFamily: "'Libre Baskerville', serif", color: '#1A1A1A' }}
                    >
                        What Our Blooms Say
                    </h2>
                    <div className="grid grid-cols-3 gap-8">
                        {testimonials.map((testimonial, index) => (
                            <div
                                key={index}
                                className="bg-white rounded-2xl p-8 transition-all duration-300"
                                style={{ border: '1px solid #F9A8D4', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
                                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 8px 30px rgba(190,24,93,0.1)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'; }}
                            >
                                <div className="flex gap-1 mb-4">
                                    {[...Array(testimonial.rating)].map((_, i) => (
                                        <Star key={i} className="w-4 h-4" style={{ color: '#BE185D', fill: '#BE185D' }} />
                                    ))}
                                </div>
                                <p className="text-sm leading-relaxed mb-6" style={{ color: '#1A1A1A', fontStyle: 'italic' }}>
                                    "{testimonial.text}"
                                </p>
                                <div>
                                    <p className="text-sm font-semibold" style={{ color: '#BE185D' }}>{testimonial.name}</p>
                                    <p className="text-xs" style={{ color: '#6B7280' }}>{testimonial.city}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 6: NEWSLETTER ===== */}
            <section className="py-12 lg:py-20" style={{ backgroundColor: '#BE185D' }}>
                <div className="max-w-2xl mx-auto px-4 text-center">
                    <h2
                        className="text-2xl lg:text-3xl font-bold mb-3"
                        style={{ fontFamily: "'Libre Baskerville', serif", color: '#FFFFFF' }}
                    >
                        Stay in Bloom
                    </h2>
                    <p className="text-sm mb-8" style={{ color: 'rgba(255,255,255,0.8)' }}>
                        Subscribe to our newsletter for exclusive collections, early access, and style inspiration.
                    </p>
                    <form
                        onSubmit={handleSubscribe}
                        className="flex gap-0 max-w-md mx-auto mehak-home-newsletter"
                    >
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Your email address"
                            required
                            className="flex-1 px-5 py-3.5 bg-white rounded-l-lg text-sm outline-none"
                            style={{ color: '#1A1A1A', fontFamily: "'DM Sans', sans-serif", border: 'none' }}
                        />
                        <button
                            type="submit"
                            className="px-6 py-3.5 rounded-r-lg text-sm font-semibold uppercase tracking-wider transition-colors"
                            style={{ backgroundColor: '#9D174D', color: '#FFFFFF', border: 'none', fontFamily: "'DM Sans', sans-serif" }}
                            onMouseEnter={(e) => (e.target.style.backgroundColor = '#831843')}
                            onMouseLeave={(e) => (e.target.style.backgroundColor = '#9D174D')}
                        >
                            Subscribe
                        </button>
                    </form>
                </div>
                <style>{`
                    @media (max-width: 640px) {
                        .mehak-home-newsletter {
                            flex-direction: column !important;
                            gap: 8px !important;
                        }
                        .mehak-home-newsletter input {
                            border-radius: 8px !important;
                        }
                        .mehak-home-newsletter button {
                            border-radius: 8px !important;
                        }
                    }
                `}</style>
            </section>
        </div>
    );
};

export default Template8;
